import { NineButton, NineHeading } from "@9amhealth/wcl/generated/react";
import { keyframes } from "@emotion/css";
import styled from "@emotion/styled";
import type { FC } from "react";
import React from "react";
import { appViewState, useBloc } from "src/state/state";
import BlockingLoadingOverlayController, {
  BlockingLoadingOverlayBloc
} from "src/ui/components/BlockingLoadingOverlay/BlockingLoadingOverlayController";
import LoaderIconImage from "./LoadingIconSmall.webp";
import LoaderIconImagePartner from "./LoadingIconSmallPartner.webp";
import LoadingCompleteIconImage from "./LoadingComplete.svg";
import translate from "src/lib/translate";
import Translate from "src/ui/components/Translate/Translate";
import AppViewCubit from "src/state/AppViewCubit/AppViewCubit";
import Logo from "src/ui/assets/icons/Logo";
import { TranslationKey } from "src/types/translationKey";
import { useBlocNext } from "@blac/react";
import { AnimatePresence, motion, type Variants } from "framer-motion";

const Fullpage = styled(motion.div)`
  label: BlockingLoadingOverlayFullpage;
  position: fixed;
  top: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.65);
  z-index: 99999999;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100vw;
  height: 100vh;
  height: 100svh;
  --spinner-scale: 1;

  &[data-status="error"] {
    background-color: rgba(0, 0, 0, 0.75);
  }

  &[data-bg="solid"] {
    background: #000;
    &::before {
      content: "";
      position: absolute;
      inset: 0;
      opacity: 0.4;
      background: linear-gradient(
        60deg,
        var(--color-sunrise-blue) 13.49%,
        var(--color-sunrise-pink) 51.22%,
        var(--color-sunrise-orange) 85.11%
      );
    }
  }

  &[data-bg="partner"] {
    background: var(--color-cream);
    --spinner-scale: 0.6;

    #branding-logo {
      display: flex;
    }
  }

  &[data-bg="branded"] {
    background: var(--color-cream);
    --logo-pos: calc(var(--safe-area-inset-top, 0) + 1.8rem);

    #branding-logo {
      display: flex;
    }
  }

  #branding-logo {
    display: none;
  }
`;

const ErrorContent = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: min(100%, 370px);
  padding: 0 1.6rem;
  position: relative;
  z-index: 10;

  .color {
    color: white;
  }

  h3,
  p {
    text-wrap: balance;
  }
`;

const SpinKeyframes = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const ContentWrap = styled(motion.div)`
  position: absolute;
  transform: scale(var(--spinner-scale, 1));
  text-align: center;

  h3,
  p {
    font-family: var(--font-family);
    color: var(--Greys-White, #fff);
    letter-spacing: -0.36px;
    padding: 0 1.6rem;
  }

  h3 {
    font-weight: 500;
    margin-top: 0.8rem;
    font-size: 1.25rem;
  }

  p {
    font-weight: 400;
    font-size: 1rem;
    font-family: var(--font-family);
  }

  img {
    display: block;
    width: 4rem;
    height: 4rem;
    margin: 0 auto;
  }

  &.loading-progress img {
    animation: ${SpinKeyframes} 1.2s linear infinite;
  }
`;

const NineLogo = styled(motion.div)`
  position: absolute;
  top: var(--logo-pos, calc(50svh - 4.5rem));
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;

  svg {
    display: block;
    transform: scale(1.2);
  }
`;

const overlayVariants: Variants = {
  hidden: {
    opacity: 0,
    backdropFilter: "blur(0px)"
  },
  visible: {
    opacity: 1,
    backdropFilter: "blur(4px)",
    transition: {
      duration: 0.3,
      ease: "easeInOut"
    }
  },
  exit: {
    opacity: 0,
    backdropFilter: "blur(0px)",
    transition: {
      duration: 0.25,
      ease: "easeInOut"
    }
  }
};

const contentVariants: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.9
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1]
    }
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: {
      duration: 0.2,
      ease: "easeIn"
    }
  }
};

const successVariants: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.8
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: [0.34, 1.56, 0.64, 1]
    }
  },
  exit: {
    opacity: 0,
    scale: 0.9,
    transition: {
      duration: 0.2,
      ease: "easeIn"
    }
  }
};

const errorVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 16
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.4, 0, 0.2, 1]
    }
  },
  exit: {
    opacity: 0,
    y: -8,
    transition: {
      duration: 0.2,
      ease: "easeIn"
    }
  }
};

const logoVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.3, delay: 0.1 }
  },
  exit: {
    opacity: 0,
    transition: { duration: 0.2 }
  }
};

export const LoadingSpinner: FC<{ style?: React.CSSProperties }> = (props) => {
  const [{ title, message, status }] = useBloc(BlockingLoadingOverlayBloc, {
    create: () => BlockingLoadingOverlayController
  });

  const loadingText = title
    ? translate(title as TranslationKey)
    : translate("loading");
  const messageText = message ? translate(message as TranslationKey) : "";

  const screenReaderText = [loadingText, messageText]
    .filter(Boolean)
    .join(". ");

  const [liveRegionContent, setLiveRegionContent] = React.useState("");

  React.useEffect(() => {
    setLiveRegionContent("");

    const timer = setTimeout(() => {
      if (screenReaderText) {
        setLiveRegionContent(screenReaderText);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [screenReaderText]);

  const isActive = status === "loading";

  return (
    <ContentWrap
      className="loading-progress"
      style={props.style}
      role={isActive ? "status" : undefined}
      tabIndex={isActive ? -1 : undefined}
      aria-hidden={!isActive}
      variants={contentVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
    >
      <img
        src={
          appViewState.state.partnerStyles
            ? LoaderIconImagePartner
            : LoaderIconImage
        }
        alt=""
        aria-hidden="true"
      />
      {title && (
        <h3 className="as-h4-large color">
          {translate(title as TranslationKey)}
        </h3>
      )}
      {message && (
        <>
          <nine-spacer s="xs"></nine-spacer>
          <p className="m0 color">{translate(message as TranslationKey)}</p>
        </>
      )}

      <div className="only-sr" aria-live="polite" aria-atomic="true">
        {liveRegionContent}
      </div>
    </ContentWrap>
  );
};

const BlockingLoadingOverlay: FC = () => {
  const [{ status, error, bg, fadeIn, title, message }] = useBloc(
    BlockingLoadingOverlayBloc,
    {
      create: () => BlockingLoadingOverlayController
    }
  );
  const [{ partnerSource }] = useBlocNext(AppViewCubit);

  const handleRetry = () => {
    BlockingLoadingOverlayController.callErrorRetry();
  };

  const handleCancel = () => {
    BlockingLoadingOverlayController.callOnCancel();
    BlockingLoadingOverlayController.endLoading();
  };

  const isVisible = status !== "none";
  const isAriaHidden = status === "none" || status === "success";
  const showLogo = bg === "branded" || bg === "partner";

  return (
    <AnimatePresence mode="wait">
      {isVisible && (
        <Fullpage
          key="blocking-overlay"
          aria-hidden={isAriaHidden}
          data-status={status}
          data-bg={bg}
          data-partner={partnerSource}
          variants={fadeIn ? overlayVariants : undefined}
          initial={fadeIn ? "hidden" : "visible"}
          animate="visible"
          exit="exit"
        >
          <AnimatePresence mode="wait">
            {showLogo && (
              <NineLogo
                key="branding-logo"
                id="branding-logo"
                variants={logoVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <Logo />
              </NineLogo>
            )}
          </AnimatePresence>

          <AnimatePresence mode="wait">
            {status === "loading" && <LoadingSpinner key="loading-spinner" />}

            {status === "success" && (
              <ContentWrap
                key="success-content"
                variants={successVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <img
                  src={LoadingCompleteIconImage}
                  alt={translate("loadingCompleted")}
                />
                {title && (
                  <h3 className="as-h4-large color">
                    {translate(title as TranslationKey)}
                  </h3>
                )}
                {message && (
                  <>
                    <nine-spacer s="xs"></nine-spacer>
                    <p className="m0 color">
                      {translate(message as TranslationKey)}
                    </p>
                  </>
                )}
              </ContentWrap>
            )}

            {status === "error" && error && (
              <ErrorContent
                key="error-content"
                variants={errorVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <NineHeading>
                  <h3 className="as-h4-large color">
                    {error.title ?? translate("error.generic.short")}
                  </h3>
                  <nine-spacer s="xs"></nine-spacer>
                  <p className="m0 color">
                    {error.message ?? translate("error.generic.short.tryAgain")}
                  </p>
                </NineHeading>
                <nine-spacer s="md"></nine-spacer>
                {error.retry && (
                  <NineButton onClick={handleRetry}>
                    <Translate msg="tryAgain" />
                  </NineButton>
                )}
                <nine-spacer s="xs"></nine-spacer>

                <NineButton
                  onClick={handleCancel}
                  arrow=""
                  variant={error.retry ? "ghost" : "fill"}
                  style={{ color: error.retry ? "white" : undefined }}
                  padding="equal"
                >
                  <Translate msg={error.cancelText ?? "cancel"} />
                </NineButton>
              </ErrorContent>
            )}
          </AnimatePresence>
        </Fullpage>
      )}
    </AnimatePresence>
  );
};

export default BlockingLoadingOverlay;
